import { memoizeFunction } from '@agilepoint/utils-common';
import { RefObject } from 'react';
import { OnResize } from 'react-moveable';
import styled, { css, CSSProperties } from 'styled-components';
import { IResizeDirection, Moveable } from '../../moveable';
import { IBaseComponentProps } from '../../types';
import { ISliderValue } from '../slider-types';
import { selectStyleByState } from '../utils';
import { clamp } from '../../../utils';

interface IKnobProps {
    $isHorizontallyAligned: boolean;
}

const KnobBase = styled.span<IKnobProps>`
    display: inline-block;
    position: absolute;
    transform-origin: center;
    transform: translate(-50%, -50%);
    border-radius: ${({ theme: { slider: { composites: { knob } } } }) => knob.borderRadius};
    height: ${({ theme: { slider: { composites: { knob } } } }) => knob.thickness};
    width: ${({ theme: { slider: { composites: { knob } } } }) => knob.thickness};
`;

const LeftKnob = styled(KnobBase)`
    top: ${({ $isHorizontallyAligned }) => $isHorizontallyAligned ? '50%' : '100%'};
    left: ${({ $isHorizontallyAligned }) => $isHorizontallyAligned ? '0' : '50%'};
`;

const RightKnob = styled(KnobBase)`
    top: ${({ $isHorizontallyAligned }) => $isHorizontallyAligned ? '50%' : '0%'};
    left: ${({ $isHorizontallyAligned }) => $isHorizontallyAligned ? '100%' : '50%'};
`;

interface ISelectionKnobsProps extends IBaseComponentProps {
    disabled?: boolean;
    isHorizontallyAligned: boolean;
    hovered?: boolean;
    start: number;
    end: number;
    unitSize: number;
    inRangeMode: boolean;
    onStartChange(startPosition: number): void;
    onEndChange(endPosition: number): void;
}

interface IDimension extends Pick<CSSProperties, 'bottom' | 'right' | 'top' | 'left' | 'width' | 'height'> {
}

const calculateDimension = (value: ISliderValue, isHorizontallyAligned?: boolean): IDimension => {
    const { end, start = 0 } = value;
    if (isHorizontallyAligned) {
        return {
            left: `${start}%`,
            right: `${end}%`,
            height: '100%'
        };
    }
    return {
        top: `${end}%`,
        bottom: `${start}%`,
        width: '100%'
    };
};

const getDirection = memoizeFunction((isHorizontallyAligned?: boolean, inRangeMode?: boolean): IResizeDirection[] => {
    if (inRangeMode) {
        return isHorizontallyAligned ? ['e', 'w'] : ['n', 's'];
    }
    return isHorizontallyAligned ? ['e'] : ['n'];
});

function SelectionKnobsBase(props: ISelectionKnobsProps) {
    const {
        elementRef, className, inRangeMode, isHorizontallyAligned, disabled,
        end, start, unitSize, onEndChange, onStartChange
    } = props;

    const dimension = calculateDimension({ start, end }, isHorizontallyAligned);

    const clampPosition = (position: number, min: number, max: number, isEnd?: boolean) => {
        if (inRangeMode) {
            return clamp(position, min + (isEnd ? unitSize : 0), max - (isEnd ? 0 : unitSize));
        }
        return clamp(position, min, max);
    };

    const onResize = (ev: OnResize) => {
        const wrapper = ev.target.parentElement;
        if (!wrapper) {
            return;
        }
        const { left, width, height, top } = wrapper.getBoundingClientRect();
        const direction = ev.direction[isHorizontallyAligned ? 0 : 1];
        let position = 0;
        if (isHorizontallyAligned) {
            position = ((ev.clientX - left) / width) * 100;
            if (direction === 1) {
                onEndChange(clampPosition(position, start, 100, true));
                return;
            }
            onStartChange(clampPosition(position, 0, 100 - end));
            return;
        }
        position = ((height + top - ev.clientY) / height) * 100;
        if (direction === -1) {
            onEndChange(clampPosition(position, start, 100, true));
            return;
        }
        onStartChange(clampPosition(position, 0, 100 - end));
    };

    return (
        <Moveable elementRef={elementRef} resizable={!disabled} onResize={onResize} hideDefaultLines
            highlightMoveable={false} renderDirections={getDirection(isHorizontallyAligned, inRangeMode)}>{
                (ref) => (
                    <div className={className} ref={ref as RefObject<HTMLDivElement>} style={dimension}>
                        {inRangeMode ? <LeftKnob $isHorizontallyAligned={isHorizontallyAligned} /> : null}
                        <RightKnob $isHorizontallyAligned={isHorizontallyAligned} />
                    </div>
                )
            }</Moveable>
    );
}

export const SelectionKnobs = styled(SelectionKnobsBase).attrs({
    className: String('ap-slider-selectionKnobs')
})`
    position: absolute;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    border-radius: ${({ theme: { slider: { composites: { slideArea } } } }) => slideArea.borderRadius};
    background-color: ${({ theme, disabled }) => selectStyleByState(theme, disabled ? 'disabled' : 'default').slideArea.filledAreaBackground};

    ${LeftKnob}, ${RightKnob} {
        background-color: ${({ theme, disabled }) => selectStyleByState(theme, disabled ? 'disabled' : 'default').knob.backgroundColor};
        ${({ theme, disabled, hovered }) => hovered && css`box-shadow: 0px 0px 17px ${selectStyleByState(theme, disabled ? 'disabled' : 'default').knob.backgroundColor}`};
    }
`;
